import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  SectionList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Modal,
  TextInput,
  Image,
  ScrollView,
  Pressable,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
// Drag&Drop wird mit TouchableOpacity und onLongPress implementiert
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { getAllShoppingLists, purchaseItem, deleteItem, updateItem, moveItemToStore, reorderItems } from '../services/shoppingService';
import { getStores } from '../services/storesService';
import { selectAndUploadImage } from '../services/uploadService';
import useOfflineStatus from '../hooks/useOfflineStatus';
import cacheService from '../services/cacheService';
import { useAuth } from '../contexts/AuthContext';
import { checkForUpdates, markVersionAsNotified } from '../services/versionService';

const ShoppingScreen = ({ navigation, route }) => {
  const { user } = useAuth();
  

  // Hilfsfunktion: Formatiert quantity (entfernt .00 bei reinen Zahlen, lässt Text unverändert)
  const formatQuantity = (quantity) => {
    if (!quantity && quantity !== 0) return '1';
    
    const quantityStr = String(quantity);
    
    // Prüfe ob es eine reine Zahl ist (nur Ziffern, optional mit . und Nachkommastellen)
    // WICHTIG: /^\d+(\.\d+)?$/ prüft ob der String nur eine Zahl ist, nicht z.B. "1-2" oder "ca. 500"
    if (/^\d+(\.\d+)?$/.test(quantityStr.trim())) {
      const numValue = parseFloat(quantityStr);
      // Wenn es eine ganze Zahl ist (oder .00 am Ende hat), zeige nur die ganze Zahl
      if (numValue % 1 === 0) {
        return String(Math.floor(numValue));
      }
      // Falls es echte Nachkommastellen hat (z.B. 1.5), zeige sie an
      return String(numValue);
    }
    
    // Ansonsten: Text unverändert zurückgeben (z.B. "1-2", "ca. 500g")
    return quantityStr;
  };

  // Reagiere auf Header-Buttons
  useEffect(() => {
    if (route.params?.toggleSearch) {
      setShowSearch(!showSearch);
      // Parameter zurücksetzen
      navigation.setParams({ toggleSearch: false });
    }
  }, [route.params?.toggleSearch]);

  useEffect(() => {
    if (route.params?.refresh) {
      onRefresh();
      // Parameter zurücksetzen
      navigation.setParams({ refresh: false });
    }
  }, [route.params?.refresh]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // {id, article_id, quantity, tags, notes, storeId}
  const [newTagText, setNewTagText] = useState(''); // Für das Tag-Eingabefeld
  const [showImageModal, setShowImageModal] = useState(false); // Für Bildanzeige
  const [selectedImageUrl, setSelectedImageUrl] = useState(''); // URL des anzuzeigenden Bildes
  const [stores, setStores] = useState([]); // Für Laden-Auswahl
  const [showStoreModal, setShowStoreModal] = useState(false); // Für Laden-Auswahl-Modal
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  
  // Auto-Update: Wenn Modal erscheint, automatisch Update durchführen
  useEffect(() => {
    if (showUpdateModal && updateInfo && Platform.OS === 'web') {
      const performAutoUpdate = async () => {
        try {
          // 1. Prüfe ob Offline-Änderungen vorhanden sind
          const cacheServiceModule = await import('../services/cacheService');
          const syncServiceModule = await import('../services/syncService');
          const cacheService = cacheServiceModule.default;
          const syncService = syncServiceModule.default;
          
          const queue = await cacheService.getSyncQueue();
          const offlineChanges = await cacheService.getOfflineChanges();
          
          if (queue.length > 0 || offlineChanges.length > 0) {
            console.log(`⚠️ ${queue.length} Änderungen werden vor dem Update synchronisiert...`);
            
            // Synchronisiere alle Änderungen
            await syncService.syncAll();
            
            // Warte kurz, um sicherzustellen dass alle Requests fertig sind
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            console.log('✅ Alle Änderungen synchronisiert');
          }
          
          // 2. Markiere Version als benachrichtigt
          await markVersionAsNotified(updateInfo.serverVersion);
          
          // 3. Jetzt ist es sicher zu reloaden
          console.log('✅ Update wird durchgeführt - Daten sind sicher!');
          
          // Kurze Verzögerung damit User die Meldung sieht
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          window.location.reload(true); // Hard reload
        } catch (error) {
          console.error('❌ Fehler beim Auto-Update:', error);
          setShowUpdateModal(false);
          
          // Zeige Fehler an
          if (window.confirm(`Update-Fehler: ${error.message || 'Unbekannter Fehler'}\n\nApp wird trotzdem aktualisiert. Offline-Änderungen könnten verloren gehen. Fortfahren?`)) {
            window.location.reload(true);
          }
        }
      };
      
      // Start Auto-Update nach kurzer Verzögerung (damit Modal sichtbar wird)
      setTimeout(performAutoUpdate, 500);
    }
  }, [showUpdateModal, updateInfo]);
  
  // Offline-Status
  const { isOnline, isOffline, isSyncing, hasOfflineChanges, refreshOfflineChanges } = useOfflineStatus();
  
  // Sortierungs-Funktion basierend auf User-Präferenzen
  // WICHTIG: Verwende immer den aktuellen user-State, nicht eine alte Referenz
  const sortItems = useCallback((items, sortingOverride = null) => {
    // WICHTIG: Verwende aktuellen user-State (kann sich ändern)
    // sortingOverride erlaubt es, die Sortierung explizit zu übergeben (z.B. beim Laden)
    const sorting = sortingOverride || user?.shoppingListSorting || 'smart';
    
    // Kopiere Array, um Mutation zu vermeiden
    const sortedItems = [...items];
    
    if (sorting === 'alphabetical') {
      // Einfache alphabetische Sortierung - ignoriert sort_order komplett
      return sortedItems.sort((a, b) => {
        // Erst nach is_purchased sortieren (false vor true)
        if (a.is_purchased !== b.is_purchased) {
          return a.is_purchased ? 1 : -1;
        }
        // Dann alphabetisch nach Artikelname
        return (a.article_name || '').localeCompare(b.article_name || '');
      });
    } else {
      // Manuelle Sortierung (Standard) - NUR nach sort_order
      // WICHTIG: learned_position NICHT verwenden, sonst wird Liste bei jedem Hinzufügen/Abhaken neu sortiert!
      // Die learned_position wird nur beim ersten Hinzufügen verwendet, um die initiale sort_order zu setzen.
      return sortedItems.sort((a, b) => {
        // Erst nach is_purchased sortieren (false vor true)
        if (a.is_purchased !== b.is_purchased) {
          return a.is_purchased ? 1 : -1;
        }
        
        // Dann nach sort_order (manuelle Sortierung, die durch Drag&Drop verändert wird)
        if (a.sort_order !== b.sort_order) {
          return a.sort_order - b.sort_order;
        }
        
        // Schließlich nach Name (als Fallback)
        return (a.article_name || '').localeCompare(b.article_name || '');
      });
    }
  }, [user?.shoppingListSorting]);
  
  // Offline-Änderungen nach Sync aktualisieren
  useEffect(() => {
    // Nur wenn nicht gerade der erste Load läuft (sonst würde _isNew Markierungen entfernt)
    // UND wenn die Listen bereits geladen wurden (lists.length > 0)
    if (isOnline && !isSyncing && !isFirstLoad && lists.length > 0) {
      // Nach erfolgreichem Sync: UI neu laden um Indikatoren zu entfernen
      setTimeout(() => {
        // Erst Offline-Änderungen aktualisieren
        refreshOfflineChanges();
        
        // Dann Listen neu laden OHNE Offline-Änderungen anzuwenden
        loadListsWithoutOfflineChanges();
      }, 2000); // Länger warten, damit _isNew Markierungen nicht überschrieben werden
    }
  }, [isOnline, isSyncing, hasOfflineChanges, isFirstLoad, lists.length]);

  // Listen neu sortieren wenn sich User-Präferenzen ändern
  // WICHTIG: Nur neu sortieren wenn sich die Sortierungs-Präferenz wirklich ändert (nicht beim ersten Laden)
  const prevSortingRef = useRef(user?.shoppingListSorting);
  useEffect(() => {
    // Nur sortieren wenn:
    // 1. Liste bereits geladen wurde (nicht beim ersten Load)
    // 2. User-Präferenz vorhanden ist
    // 3. Präferenz sich wirklich geändert hat (nicht beim ersten Laden)
    if (lists.length > 0 && user?.shoppingListSorting && !isFirstLoad && prevSortingRef.current !== user?.shoppingListSorting) {
      // WICHTIG: _isNew Markierungen beim Sortieren beibehalten
      const existingNewItemIds = new Set();
      const existingPendingSyncItems = new Set();
      lists.forEach(store => {
        store.items.forEach(item => {
          if (item._isNew) {
            existingNewItemIds.add(item.id);
          }
          if (item._needsSync) {
            existingPendingSyncItems.add(item.id);
          }
        });
      });
      
      const listsWithUserSorting = lists.map(store => ({
        ...store,
        items: sortItems(store.items).map(item => ({
          ...item,
          _isNew: existingNewItemIds.has(item.id), // _isNew Markierungen beibehalten
          _needsSync: existingPendingSyncItems.has(item.id) // _needsSync Markierungen beibehalten
        }))
      }));
      setLists(listsWithUserSorting);
      prevSortingRef.current = user?.shoppingListSorting;
    } else if (user?.shoppingListSorting) {
      // Beim ersten Laden: Nur die Referenz aktualisieren, nicht sortieren
      prevSortingRef.current = user?.shoppingListSorting;
    }
  }, [user?.shoppingListSorting, sortItems, lists.length, isFirstLoad]);

  
  // Drag&Drop State
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverItem, setDraggedOverItem] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showDragEffects, setShowDragEffects] = useState(false); // Für visuelle Effekte
  const [dragStartTime, setDragStartTime] = useState(null);
  const [dragTimeout, setDragTimeout] = useState(null);
  
  // Prüfe ob Drag&Drop aktiviert ist (nur bei intelligenter Sortierung)
  const isDragDropEnabled = user?.shoppingListSorting !== 'alphabetical';
  const [forceUpdate, setForceUpdate] = useState(0);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [pressStartTime, setPressStartTime] = useState(null);
  const [pressTimeout, setPressTimeout] = useState(null);
  const [swipeStartX, setSwipeStartX] = useState(null);
  const [swipeStartY, setSwipeStartY] = useState(null);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [dropZonePosition, setDropZonePosition] = useState(null); // {itemId, position: 'above'|'below'}
  
  // Optimistic UI und Sync-Status
  const [pendingSyncItems, setPendingSyncItems] = useState(new Set()); // IDs von Items die synchronisiert werden müssen
  const [syncInProgress, setSyncInProgress] = useState(false);
  
  // Für Refresh-Feedback: Vorherige Item-IDs speichern
  const [previousItemIds, setPreviousItemIds] = useState(new Set()); // Item-IDs vor dem Refresh
  const [isFirstLoad, setIsFirstLoad] = useState(true); // Track ob es der erste Load nach App-Start ist

  useFocusEffect(
    useCallback(() => {
      // Beim App-Start: Mit Feedback laden (um neue Artikel zu erkennen)
      if (isFirstLoad) {
        // Beim ersten Load: WICHTIG - erst IDs aus AsyncStorage laden, dann loadLists aufrufen
        const loadWithSavedIds = async () => {
          try {
            const storedIds = await AsyncStorage.getItem('last_shopping_list_item_ids');
            
            // IDs parsen - sicherstellen dass es ein Array ist
            let savedItemIds = new Set();
            if (storedIds) {
              try {
                const parsedIds = JSON.parse(storedIds);
                // Sicherstellen dass es ein Array ist
                if (Array.isArray(parsedIds)) {
                  savedItemIds = new Set(parsedIds);
                } else {
                  // Fallback falls altes Format
                  savedItemIds = new Set();
                }
              } catch (parseError) {
                console.error('Error parsing stored IDs:', parseError);
                savedItemIds = new Set();
              }
            }
            
            // State setzen
            setPreviousItemIds(savedItemIds);
            
            // WICHTIG: Load mit Feedback und savedItemIds als Parameter übergeben
            // Das stellt sicher, dass die IDs für den Vergleich verwendet werden
            // Verwende savedItemIds direkt als Parameter (nicht aus State)
            await loadLists(true, savedItemIds);
            
            setIsFirstLoad(false);
            
            // Versionsprüfung beim App-Start
            checkForUpdates().then(result => {
              if (result.hasUpdate) {
                setUpdateInfo(result);
                setShowUpdateModal(true);
              }
            }).catch(error => {
              console.error('Update check failed:', error);
            });
          } catch (error) {
            console.error('Error loading saved IDs:', error);
            // Wenn kein gespeicherter State vorhanden: Normal laden
            await loadLists(true, new Set());
            setIsFirstLoad(false);
          }
        };
        
        loadWithSavedIds();
      } else {
        // Bei nachfolgenden Focus-Events: Normal laden ohne Feedback
        loadLists();
      }
    }, [isFirstLoad])
  );

  // Verhindere Scrollen während des Drags
  useEffect(() => {
    if (isDragging) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isDragging]);


  const loadLists = async (showFeedback = false, previousIdsOverride = null) => {
    try {
      // Läden und Einkaufslisten parallel laden
      // forceRefresh: true um immer frische Daten zu bekommen
      const [listsResult, storesResult] = await Promise.all([
        getAllShoppingLists({ forceRefresh: true }),
        getStores()
      ]);
      
      if (listsResult.success) {
        // Nach Name sortieren
        const sortedLists = listsResult.data.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        // Debug-Log entfernt - war hier
        
        // Offline-Änderungen anwenden
        const listsWithOfflineChanges = await applyOfflineChanges(sortedLists);
        
        // WICHTIG: Sortierung basierend auf User-Einstellung anwenden
        // Wenn alphabetische Sortierung gewählt ist, muss sortiert werden
        // Bei manueller Sortierung: Backend-Sortierung beibehalten (sort_order)
        const sorting = user?.shoppingListSorting || 'smart';
        const listsWithUserSorting = sorting === 'alphabetical'
          ? listsWithOfflineChanges.map(store => ({
              ...store,
              items: sortItems(store.items, sorting)
            }))
          : listsWithOfflineChanges; // Bei manueller Sortierung: Backend-Sortierung beibehalten
        
        // Alle aktuellen Item-IDs sammeln (immer für nächsten Refresh und App-Start)
        const currentItemIds = new Set();
        listsWithUserSorting.forEach(store => {
          store.items.forEach(item => {
            currentItemIds.add(item.id);
          });
        });
        
        // Wenn Feedback gewünscht: Neue Items erkennen und Alert anzeigen
        if (showFeedback) {
          // Verwende override-Parameter wenn vorhanden (direkt aus onRefresh übergeben)
          // WICHTIG: previousIdsOverride hat Vorrang, da es direkt aus AsyncStorage kommt (App-Start)
          const idsToCompare = previousIdsOverride !== null ? previousIdsOverride : previousItemIds;
          
          // Neue Items finden
          // WICHTIG: Auch wenn idsToCompare leer ist, sollten wir prüfen ob es neue Items gibt
          // Das kann passieren, wenn die IDs nicht korrekt geladen wurden oder beim ersten Start
          // Aber: Nur wenn idsToCompare nicht leer ist, können wir zuverlässig neue Items erkennen
          const newItemIds = idsToCompare.size > 0
            ? new Set(Array.from(currentItemIds).filter(id => !idsToCompare.has(id)))
            : new Set();
          
          // Bestehende _isNew Markierungen aus vorherigem State berücksichtigen
          const existingNewItemIds = new Set();
          // Verwende aktuellen lists State (kann noch leer sein beim ersten Load)
          if (lists.length > 0) {
            lists.forEach(store => {
              store.items.forEach(item => {
                if (item._isNew) {
                  existingNewItemIds.add(item.id);
                }
              });
            });
          }
          
          // Items, die bereits _isNew waren, aber jetzt nicht mehr neu sind, verlieren die Markierung
          // Items, die weiterhin neu sind, behalten die Markierung
          const finalNewItemIds = new Set();
          
          // Alle Items, die in dieser Refresh-Runde neu sind, bekommen/behalten _isNew
          newItemIds.forEach(id => finalNewItemIds.add(id));
          
          // Items, die bereits _isNew waren, behalten die Markierung nur wenn sie immer noch neu sind
          existingNewItemIds.forEach(id => {
            if (newItemIds.has(id)) {
              // War bereits neu und ist immer noch neu -> behalte Markierung
              finalNewItemIds.add(id);
            }
            // War bereits neu, aber ist jetzt nicht mehr neu -> verliere Markierung (nicht in finalNewItemIds)
          });
          
          // Neue Items mit _isNew markieren (oder Markierung entfernen wenn nicht mehr neu)
          const listsWithNewItems = listsWithUserSorting.map(store => ({
            ...store,
            items: store.items.map(item => ({
              ...item,
              _isNew: finalNewItemIds.has(item.id)
            }))
          }));
          setLists(listsWithNewItems);
          
          // Neue Items für Alert-Meldung sammeln (nur die, die in diesem Refresh neu hinzugekommen sind)
          // WICHTIG: Nur wenn idsToCompare nicht leer ist, können wir zuverlässig neue Items erkennen
          // Wenn idsToCompare leer ist, bedeutet das entweder:
          // 1. Erster Start der App (dann sollten wir keine "neuen" Items anzeigen)
          // 2. IDs wurden nicht korrekt geladen (dann sollten wir trotzdem versuchen, neue Items zu erkennen)
          // Lösung: Wenn idsToCompare leer ist, aber es gibt bereits gespeicherte IDs in AsyncStorage,
          // dann sollten wir diese verwenden (das passiert beim App-Start)
          const newItems = [];
          if (idsToCompare.size > 0) {
            // Normale Erkennung: Vergleiche mit vorherigen IDs
            listsWithUserSorting.forEach(store => {
              store.items.forEach(item => {
                if (newItemIds.has(item.id)) {
                  newItems.push({
                    name: item.article_name,
                    store: store.name,
                    created_by: item.created_by_name || 'Unbekannt'
                  });
                }
              });
            });
          } else {
            // idsToCompare ist leer - könnte bedeuten, dass IDs nicht korrekt geladen wurden
            // Versuche, die IDs aus AsyncStorage zu laden und zu vergleichen
            // Das ist ein Fallback für den Fall, dass previousIdsOverride nicht korrekt übergeben wurde
            try {
              const storedIds = await AsyncStorage.getItem('last_shopping_list_item_ids');
              if (storedIds) {
                const parsedIds = JSON.parse(storedIds);
                if (Array.isArray(parsedIds) && parsedIds.length > 0) {
                  const storedIdsSet = new Set(parsedIds);
                  const fallbackNewItemIds = new Set(Array.from(currentItemIds).filter(id => !storedIdsSet.has(id)));
                  
                  listsWithUserSorting.forEach(store => {
                    store.items.forEach(item => {
                      if (fallbackNewItemIds.has(item.id)) {
                        newItems.push({
                          name: item.article_name,
                          store: store.name,
                          created_by: item.created_by_name || 'Unbekannt'
                        });
                      }
                    });
                  });
                }
              }
            } catch (error) {
              console.error('Error loading stored IDs for comparison:', error);
            }
          }
          
          // Alert mit Feedback anzeigen (immer wenn showFeedback true ist)
          const newItemsText = newItems.length > 0
            ? `\n\nNeue Artikel hinzugefügt:\n${newItems.slice(0, 5).map(item => `• ${item.name} (${item.store}) - ${item.created_by}`).join('\n')}${newItems.length > 5 ? `\n... und ${newItems.length - 5} weitere` : ''}`
            : '\n\nKeine Änderungen vorhanden.';
          
          const alertMessage = `Die Einkaufsliste wurde erfolgreich aktualisiert.${newItemsText}`;
          
          // Plattformspezifische Alert-Anzeige (wie im Rest der App - ArticlesScreen, ArticleDetailScreen)
          if (Platform.OS === 'web') {
            // Auf Web: window.alert verwenden
            window.alert(alertMessage);
          } else {
            // Auf iOS/Android: Alert.alert verwenden (wie in ArticlesScreen und ArticleDetailScreen)
            Alert.alert(
              'Liste synchronisiert',
              alertMessage
            );
          }
          
          // Aktuelle Item-IDs für nächsten Refresh speichern (auch in AsyncStorage für App-Start)
          setPreviousItemIds(currentItemIds);
          
          // WICHTIG: IMMER in AsyncStorage speichern für nächsten App-Start (nach erfolgreichem Load)
          // Das stellt sicher, dass beim nächsten App-Start die IDs vorhanden sind
          AsyncStorage.setItem('last_shopping_list_item_ids', JSON.stringify(Array.from(currentItemIds))).catch(error => {
            console.error('Error saving item IDs:', error);
          });
        } else {
          // Kein Feedback gewünscht - bestehende _isNew Markierungen beibehalten
          // Aber nur wenn es bereits welche gibt, sonst einfach setzen
          const hasExistingNewItems = lists.some(store => 
            store.items.some(item => item._isNew)
          );
          
          if (hasExistingNewItems) {
            // Bestehende _isNew Markierungen beibehalten
            const existingNewItemIds = new Set();
            lists.forEach(store => {
              store.items.forEach(item => {
                if (item._isNew) {
                  existingNewItemIds.add(item.id);
                }
              });
            });
            
            const listsWithPreservedNewItems = listsWithUserSorting.map(store => ({
              ...store,
              items: store.items.map(item => ({
                ...item,
                _isNew: existingNewItemIds.has(item.id)
              }))
            }));
            setLists(listsWithPreservedNewItems);
          } else {
            // Keine bestehenden _isNew Markierungen - einfach setzen
            setLists(listsWithUserSorting);
          }
          
          // Item-IDs für nächsten Refresh speichern
          // WICHTIG: IMMER in AsyncStorage speichern für nächsten App-Start
          // Das stellt sicher, dass beim nächsten App-Start die IDs vorhanden sind
          setPreviousItemIds(currentItemIds);
          AsyncStorage.setItem('last_shopping_list_item_ids', JSON.stringify(Array.from(currentItemIds))).catch(error => {
            console.error('Error saving item IDs:', error);
          });
        }
    } else {
        Alert.alert('Fehler', listsResult.error);
      }
      
      if (storesResult.success) {
        setStores(storesResult.data);
      } else {
        console.error('Fehler beim Laden der Läden:', storesResult.error);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      Alert.alert('Fehler', 'Fehler beim Laden der Daten');
    }
    
    setLoading(false);
    setRefreshing(false);
  };

  // Laden ohne Offline-Änderungen (nach Sync)
  const loadListsWithoutOfflineChanges = async () => {
    try {
      const [listsResult, storesResult] = await Promise.all([
        getAllShoppingLists({ forceRefresh: true }),
        getStores()
      ]);
      
      if (listsResult.success) {
        const sortedLists = listsResult.data.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        // KEINE Offline-Änderungen anwenden - alle _isOfflineChange auf false setzen
        // WICHTIG: Bestehende _isNew Markierungen beibehalten
        const existingNewItemIds = new Set();
        lists.forEach(store => {
          store.items.forEach(item => {
            if (item._isNew) {
              existingNewItemIds.add(item.id);
            }
          });
        });
        
        // WICHTIG: Sortierung basierend auf User-Einstellung anwenden
        // Wenn alphabetische Sortierung gewählt ist, muss sortiert werden
        // Bei manueller Sortierung: Backend-Sortierung beibehalten (sort_order)
        const sorting = user?.shoppingListSorting || 'smart';
        const listsWithoutOfflineChanges = sortedLists.map(store => {
          const items = store.items.map(item => ({
            ...item,
            _isOfflineChange: false,
            _isNew: existingNewItemIds.has(item.id) // _isNew Markierungen beibehalten
          }));
          
          return {
            ...store,
            items: sorting === 'alphabetical' ? sortItems(items, sorting) : items
          };
        });
        
        // WICHTIG: Sortierung wurde bereits oben basierend auf User-Einstellung angewendet
        // Bei alphabetischer Sortierung: sortiert nach Artikelname
        // Bei manueller Sortierung: Backend-Sortierung beibehalten (sort_order)
        setLists(listsWithoutOfflineChanges);
      } else {
        Alert.alert('Fehler', listsResult.error);
      }
      
      if (storesResult.success) {
        setStores(storesResult.data);
      } else {
        console.error('Fehler beim Laden der Läden:', storesResult.error);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einkaufslisten:', error);
    }
  };

  // Offline-Änderungen auf die Listen anwenden
  const applyOfflineChanges = async (lists) => {
    try {
      const offlineChanges = await cacheService.getOfflineChanges();
      
      if (offlineChanges.length === 0) {
        // Keine Offline-Änderungen: _isOfflineChange Markierungen entfernen
        return lists.map(store => ({
          ...store,
          items: store.items.map(item => ({
            ...item,
            _isOfflineChange: false
          }))
        }));
      }

      return lists.map(store => {
        // Offline-Änderungen anwenden
        const itemsWithOfflineChanges = store.items.map(item => {
          const offlineChange = offlineChanges.find(change => 
            change.type === 'PURCHASE_ITEM' && change.itemId === item.id
          );
          
          if (offlineChange) {
            return {
              ...item,
              is_purchased: offlineChange.isPurchased,
              _isOfflineChange: true // Markierung für UI
            };
          }
          
          return {
            ...item,
            _isOfflineChange: false // Keine Offline-Änderung für diesen Artikel
          };
        });

        // WICHTIG: KEINE Sortierung beim Anwenden von Offline-Änderungen!
        // Die Items behalten ihre ursprüngliche Reihenfolge vom Backend.
        // Nur lokale Änderungen (z.B. Toggle) sortieren neu.

        return {
          ...store,
          items: itemsWithOfflineChanges
        };
      });
    } catch (error) {
      console.error('Error applying offline changes:', error);
      return lists;
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    // Item-IDs vor dem Refresh speichern
    const currentItemIds = new Set();
    lists.forEach(store => {
      store.items.forEach(item => {
        currentItemIds.add(item.id);
      });
    });
    
    // WICHTIG: Zuerst in AsyncStorage speichern (für App-Start)
    await AsyncStorage.setItem('last_shopping_list_item_ids', JSON.stringify(Array.from(currentItemIds))).catch(error => {
      console.error('Error saving item IDs:', error);
    });
    
    // WICHTIG: State direkt setzen UND als Parameter an loadLists übergeben
    setPreviousItemIds(currentItemIds);
    // Load mit Feedback - currentItemIds direkt als Parameter übergeben
    loadLists(true, currentItemIds);
    
    // Im Hintergrund auf Updates prüfen (nicht-blockierend)
    checkForUpdates().then(result => {
      if (result.hasUpdate) {
        setUpdateInfo(result);
        setShowUpdateModal(true);
      }
    }).catch(error => {
      // Fehler ignorieren - keine Update-Prüfung ist kein Problem
      console.error('Update check failed:', error);
    });
  };

  // Drag&Drop Funktionen
  const handleSwipeStart = (e, item, storeId) => {
    if (!isDragDropEnabled) return;
    
    const touch = e.nativeEvent.touches[0];
    setSwipeStartX(touch.clientX);
    setSwipeStartY(touch.clientY);
    setIsSwipeActive(true);
  };

  const handleSwipeMove = (e, item, storeId) => {
    if (!isSwipeActive || !swipeStartX || !swipeStartY || !isDragDropEnabled) return;
    
    const touch = e.nativeEvent.touches[0];
    const deltaX = touch.clientX - swipeStartX;
    const deltaY = touch.clientY - swipeStartY;
    
    // Lange Swipe-Geste: mindestens 100px horizontal, max 50px vertikal
    if (Math.abs(deltaX) > 100 && Math.abs(deltaY) < 50) {
      // Kein preventDefault() um Scrollen nicht zu blockieren
      handleDragStart(item, storeId);
      setIsSwipeActive(false);
    }
  };

  const handleSwipeEnd = () => {
    setIsSwipeActive(false);
    setSwipeStartX(null);
    setSwipeStartY(null);
  };

  const handleDrop = async (targetItem, targetStoreId, position) => {
    if (!isDragging || !draggedItem) return;
    
    
    // Erstelle ein vollständiges Target-Item mit storeId und Position
    const fullTargetItem = { 
      ...targetItem, 
      storeId: targetStoreId,
      dropPosition: position
    };
    
    // Führe den Drop aus
    await handleDragEnd(fullTargetItem);
  };

  const handleDragStart = (item, storeId) => {
    // Prüfe ob Drag&Drop aktiviert ist
    if (!isDragDropEnabled) return;
    
    // Clear any existing timeout first
    if (dragTimeout) {
      clearTimeout(dragTimeout);
    }
    
    // Erst isDragging setzen, dann draggedItem
    setIsDragging(true);
    setDraggedItem({ ...item, storeId });
    setShowDragEffects(true); // Jetzt visuelle Effekte anzeigen
    setDragStartTime(Date.now());
  };

  const handleDragOver = (item, storeId, event) => {
    if (isDragging && draggedItem && draggedItem.id !== item.id) {
      setDraggedOverItem({ ...item, storeId });
    }
  };

  const handleDragEnd = async (targetItem = null) => {
    // Verwende targetItem falls vorhanden, sonst draggedOverItem
    const dropTarget = targetItem || draggedOverItem;
    
    if (!draggedItem || !dropTarget) {
      setDraggedItem(null);
      setDraggedOverItem(null);
      setIsDragging(false);
      setDragStartTime(null);
      return;
    }
    
    // Wenn dropTarget nur eine ID hat, finde das vollständige Item
    let fullTargetItem = dropTarget;
    if (dropTarget.id && !dropTarget.article_name) {
      // Finde das vollständige Item in allen Listen
      for (const store of lists) {
        const item = store.items.find(i => i.id === dropTarget.id);
        if (item) {
          fullTargetItem = { ...item, storeId: dropTarget.storeId };
          break;
        }
      }
    }
    
    if (draggedItem.id === fullTargetItem.id) {
      setDraggedItem(null);
      setDraggedOverItem(null);
      setIsDragging(false);
      setDragStartTime(null);
      return;
    }

    // Prüfen ob Artikel zwischen verschiedenen Läden verschoben wird
    if (draggedItem.storeId !== fullTargetItem.storeId) {
      // Artikel zwischen Läden verschieben
      try {
        const sourceStore = lists.find(s => s.id === draggedItem.storeId);
        const targetStore = lists.find(s => s.id === fullTargetItem.storeId);
        
        if (!sourceStore || !targetStore) {
          throw new Error('Laden nicht gefunden');
        }

        // Artikel-Daten für den neuen Laden vorbereiten
        const itemData = {
          quantity: draggedItem.quantity?.toString() || '1',
          unit: draggedItem.unit || 'Stück',
          tags: draggedItem.tags || [],
          notes: draggedItem.notes || ''
        };

        // Artikel zum neuen Laden verschieben
        const result = await moveItemToStore(draggedItem.id, dropTarget.storeId, itemData);
        
        if (result.success) {
          // Kurz warten, damit das Backend die Änderung verarbeitet hat
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Dann die aktuelle Liste laden
          const updatedListsResult = await getAllShoppingLists({ forceRefresh: true });
          const updatedTargetStore = updatedListsResult.success ? 
            updatedListsResult.data.find(s => s.id === fullTargetItem.storeId) : null;
          
          if (updatedTargetStore) {
            const targetStoreItems = updatedTargetStore.items.filter(item => !item.is_purchased);
            const targetIndex = targetStoreItems.findIndex(item => item.id === fullTargetItem.id);
            const movedItem = targetStoreItems.find(item => item.article_id === draggedItem.article_id);
            
            if (targetIndex !== -1 && movedItem) {
              // Neue Sortierreihenfolge für den Ziel-Laden berechnen
              const newTargetItems = [...targetStoreItems];
              
              // Entferne das verschobene Item von seiner aktuellen Position
              const currentIndex = newTargetItems.findIndex(item => item.id === movedItem.id);
              if (currentIndex !== -1) {
                newTargetItems.splice(currentIndex, 1);
              }
              
              // Berechne die gewünschte Position basierend auf dropPosition
              let insertIndex;
              if (fullTargetItem.dropPosition === 'above') {
                // Pfeil-hoch: Das verschobene Item bekommt die Position des Target-Items
                insertIndex = targetIndex;
              } else if (fullTargetItem.dropPosition === 'below') {
                // Pfeil-runter: Das verschobene Item bekommt die Position des Target-Items +1
                insertIndex = targetIndex + 1;
              } else {
                // Fallback: Einfügen nach dem Target-Artikel
                insertIndex = targetIndex + 1;
              }
              
              // Stelle sicher, dass der Index gültig ist
              insertIndex = Math.max(0, Math.min(insertIndex, newTargetItems.length));
              
              // Füge das Item an der gewünschten Position ein
              newTargetItems.splice(insertIndex, 0, movedItem);
              
              // Sortierreihenfolge aktualisieren
              const reorderData = newTargetItems.map((item, index) => ({
                id: item.id,
                sort_order: index
              }));
              
              // Reorder-API aufrufen
              await reorderItems(fullTargetItem.storeId, reorderData);
            }
          }
          
          // Liste neu laden um korrekte last_used_store_name zu bekommen
          await loadLists();
          
          // Erfolgsmeldung
          Alert.alert(
            'Artikel verschoben', 
            `"${draggedItem.article_name}" wurde von ${sourceStore.name} zu ${targetStore.name} verschoben.`
          );
        } else {
          Alert.alert('Fehler', 'Fehler beim Verschieben des Artikels');
        }
      } catch (error) {
        console.error('Error moving item between stores:', error);
        Alert.alert('Fehler', 'Fehler beim Verschieben des Artikels');
      } finally {
        setDraggedItem(null);
        setDraggedOverItem(null);
        setIsDragging(false);
        setShowDragEffects(false);
        setDragStartTime(null);
        setDropZonePosition(null);
        if (dragTimeout) {
          clearTimeout(dragTimeout);
          setDragTimeout(null);
        }
      }
      return;
    }

    try {
      // Neue Sortierreihenfolge berechnen
      const store = lists.find(s => s.id === draggedItem.storeId);
      if (!store) {
        return;
      }

      const items = store.items.filter(item => !item.is_purchased); // Nur ungekaufte Items
      const draggedIndex = items.findIndex(item => item.id === draggedItem.id);
      const targetIndex = items.findIndex(item => item.id === fullTargetItem.id);

      if (draggedIndex === -1 || targetIndex === -1) {
        return;
      }

      // Items neu anordnen basierend auf der Drop-Position
      const newItems = [...items];
      
      // Berechne insertIndex BEVOR wir das Item entfernen
      let insertIndex;
      
      if (fullTargetItem.dropPosition === 'above') {
        insertIndex = targetIndex;
      } else if (fullTargetItem.dropPosition === 'below') {
        insertIndex = targetIndex + 1;
      } else {
        // Fallback: Einfügen nach dem Target-Artikel
        insertIndex = targetIndex + 1;
      }
      
      // Entferne das Item und passe insertIndex an
      const [movedItem] = newItems.splice(draggedIndex, 1);
      
      // Wenn das Item nach dem Target entfernt wurde, reduziere insertIndex
      if (draggedIndex < targetIndex) {
        insertIndex = insertIndex - 1;
      }
      
      
      // Stelle sicher, dass der Index gültig ist
      insertIndex = Math.max(0, Math.min(insertIndex, newItems.length));
      newItems.splice(insertIndex, 0, movedItem);

      // Sortierreihenfolge aktualisieren
      const reorderData = newItems.map((item, index) => ({
        id: item.id,
        sort_order: index
      }));

      // Backend aktualisieren
      const result = await reorderItems(draggedItem.storeId, reorderData);
      
      if (result.success) {
        // Lokale Liste aktualisieren - ersetze die Items direkt mit der neuen Reihenfolge
        setLists(prevLists => {
          const updatedLists = prevLists.map(store => 
            store.id === draggedItem.storeId 
              ? {
                  ...store,
                  items: [
                    // Ungekaufte Items in der neuen Reihenfolge
                    ...newItems.map((item, index) => ({
                      ...item,
                      sort_order: index
                    })),
                    // Gekaufte Items am Ende (behalten ihre ursprüngliche Reihenfolge)
                    ...store.items.filter(item => item.is_purchased)
                  ]
                }
              : store
          );
          return updatedLists;
        });
        
        // Keine forceUpdate mehr - die SectionList soll nicht neu gemountet werden
        // setForceUpdate(prev => prev + 1); // Entfernt um Scroll-Problem zu vermeiden
      } else {
        Alert.alert('Fehler', 'Fehler beim Neuordnen der Artikel');
      }
    } catch (error) {
      console.error('Error reordering items:', error);
      Alert.alert('Fehler', 'Fehler beim Neuordnen der Artikel');
    } finally {
      setDraggedItem(null);
      setDraggedOverItem(null);
      setIsDragging(false);
      setShowDragEffects(false); // Visuelle Effekte ausblenden
      setDragStartTime(null);
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }
    }
  };

  // Gefilterte Listen basierend auf Suchanfrage
  // Konvertiere die Listen zu SectionList-Format
  const sections = useMemo(() => {
    let filteredStores = lists;
    
    // Filter anwenden wenn Suchbegriff vorhanden
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredStores = lists.map(store => ({
        ...store,
        items: store.items.filter(item => {
          // Suche in Artikelname
          if (item.article_name.toLowerCase().includes(query)) {
            return true;
          }
          
          // Suche in Tags
          if (item.tags && item.tags.some(tag => 
            tag.toLowerCase().includes(query)
          )) {
            return true;
          }
          
          // Suche in Notizen
          if (item.notes && item.notes.toLowerCase().includes(query)) {
            return true;
          }
          
          return false;
        })
      })).filter(store => store.items.length > 0); // Nur Läden mit gefilterten Artikeln
    }
    
    // Konvertiere zu SectionList-Format
    return filteredStores
      .filter(store => store.items && store.items.length > 0) // Nur nicht-leere Läden
      .map(store => ({
        storeId: store.id,
        storeName: store.name,
        data: store.items
      }));
  }, [lists, searchQuery]); // forceUpdate entfernt um Scroll-Problem zu vermeiden

  const getItemCounts = (items) => {
    const total = items.length;
    const purchased = items.filter(item => item.is_purchased).length;
    const remaining = total - purchased;
    return { total, purchased, remaining };
  };

  // Rendert den Sticky Header für jeden Store
  const renderSectionHeader = ({ section }) => {
    const items = section.data;
    const totalItems = items.length;
    const purchasedItems = items.filter(item => item.is_purchased).length;
    const remainingItems = totalItems - purchasedItems;
    
    return (
      <View style={styles.storeSectionHeader}>
        <View style={styles.storeTitleContainer}>
          <Text style={styles.storeSectionTitle}>{section.storeName}</Text>
          <Text style={styles.itemCount}>
            {remainingItems > 0 
              ? `${remainingItems} ${remainingItems === 1 ? 'Artikel' : 'Artikel'}`
              : `${purchasedItems} erledigt`
            }
          </Text>
          {/* Einkauf abschließen Button - nur anzeigen wenn abgehakte Artikel vorhanden */}
          {items.some(item => item.is_purchased) && (
            <Pressable
              style={styles.completeStoreButton}
              onPress={() => handleCompleteShoppingForStore(section.storeId, section.storeName, items)}
            >
              <Ionicons name="refresh-outline" size={16} color="#fff" />
            </Pressable>
          )}
        </View>
      </View>
    );
  };


  // Escape-Handler für Drag-Modus
  const handleEscapeDrag = () => {
    if (isDragging) {
      setIsDragging(false);
      setDraggedItem(null);
      setDraggedOverItem(null);
      setShowDragEffects(false);
      setDropZonePosition(null);
    }
  };

  // Rendert ein einzelnes Item (ohne Drop-Zonen)
  const renderSectionItem = ({ item, section, index }) => {
    return (
      <View key={`item-container-${item.id}`}>
        {renderItem(item, section.storeId, section.storeName)}
      </View>
    );
  };

  const renderItem = (item, storeId, storeName) => {
    // Artikel-Daten aus den angereicherten Einkaufslisten-Daten beziehen
    const articleName = item.article_name;
    const articleTags = item.tags || [];
    const articleNotes = item.notes || '';
    const articleImageUrl = item.image_url;
    const articleQuantity = item.quantity;
    const articleUnit = item.unit;
    
    const isDragged = draggedItem && draggedItem.id === item.id;
    const isDragOver = draggedOverItem && draggedOverItem.id === item.id;
    const isNew = item._isNew || false; // Neue Artikel hervorheben
    
    
    return (
      <View 
        key={`item-wrapper-${item.id}`}
        style={{
          marginBottom: (user?.itemPadding || 12) / 2
        }}
      >
        
        <Pressable
          key={item.id}
          style={[
            styles.itemCard,
            item.is_purchased && styles.itemCardPurchased,
            isDragged && showDragEffects && styles.itemCardDragged,
            isDragOver && showDragEffects && styles.itemCardDragOver,
            isNew && styles.itemCardNew,
          ]}
          onTouchStart={(e) => handleSwipeStart(e, item, storeId)}
          onTouchMove={(e) => handleSwipeMove(e, item, storeId)}
          onTouchEnd={handleSwipeEnd}
        >
         <View style={[styles.itemTouchable, { 
           paddingVertical: item.is_purchased ? -2 : (user?.itemInnerPadding || 12),
           paddingHorizontal: item.is_purchased ? 8 : 12 
         }]}>
          <View style={styles.itemContent}>
            <View style={styles.itemMainRow}>
              <View style={styles.itemLeftColumn}>
                <View style={styles.itemNameRow}>
                  <View style={styles.itemNameWithTags}>
            <Text 
              style={[
                styles.itemName,
                item.is_purchased && styles.itemNamePurchased
              ]}
                    numberOfLines={1}
            >
              {item.is_purchased && '✓ '}
                    {articleName}
            </Text>
                    {articleImageUrl && (
                      <Pressable
                        style={styles.imageIconButton}
                        onPress={() => {
                          setSelectedImageUrl(articleImageUrl);
                          setShowImageModal(true);
                        }}
                      >
                        <Ionicons name="image" size={16} color="#666" />
                      </Pressable>
                    )}
                    {(() => {
                      const filteredTags = (articleTags || []).filter(tag => tag && tag.trim() !== '');
                      return filteredTags.length > 0 && (
                        <View style={styles.tagsContainer}>
                          {filteredTags.slice(0, 2).map((tag, index) => (
                            <View key={index} style={styles.tag}>
                              <Text style={[
                                styles.tagText,
                                item.is_purchased && styles.tagTextPurchased
                              ]}>{tag.trim()}</Text>
                            </View>
                          ))}
                        </View>
                      );
                    })()}
                  </View>
                </View>
                {articleNotes && articleNotes.trim() !== '' && (
                  <Text style={[
                    styles.itemNotes,
                    item.is_purchased && styles.itemNotesPurchased
                  ]} numberOfLines={1}>
                    {articleNotes.trim()}
                  </Text>
                )}
              </View>
              <View style={styles.quantityContainer}>
                <Text style={[
                  styles.itemQuantityNumber,
                  item.is_purchased && styles.itemQuantityNumberPurchased
                ]}>{formatQuantity(articleQuantity)}</Text>
                <Text style={[
                  styles.itemQuantityUnit,
                  item.is_purchased && styles.itemQuantityUnitPurchased
                ]}>{articleUnit}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.itemActions}>
          <Pressable
            style={styles.editButton}
            onPress={() => {
              const initialImageUrl = item.image_url || '';
              setEditingItem({
                id: item.id,
                article_id: item.article_id,
                quantity: articleQuantity?.toString() || '1',
                tags: articleTags || [],
                notes: articleNotes || '',
                storeId: storeId,
                storeName: storeName,
                articleName: articleName,
                unit: articleUnit,
                imageUrl: initialImageUrl,
                uploadingImage: false
              });
            }}
          >
            <Ionicons name="create-outline" size={20} color="#666" />
          </Pressable>

          <Pressable
            style={styles.checkboxContainer}
            onPress={() => handleToggleItem(item)}
          >
            <Ionicons
              name={item.is_purchased ? 'checkmark-circle' : 'ellipse-outline'}
              size={24}
              color={item.is_purchased ? '#4CAF50' : '#999'}
            />
            {/* Sync-Status Markierung */}
            {item._needsSync && (
              <View style={styles.offlineChangeIndicator}>
                <Ionicons name="cloud-outline" size={12} color="#FFA726" />
              </View>
            )}
          </Pressable>
          
          {/* X-Button zum Beenden des Drag-Modus - nur für den gedraggten Artikel */}
          {isDragging && isDragged && (
            <Pressable
              style={styles.dragCancelButton}
              onPress={handleEscapeDrag}
            >
              <Ionicons name="close" size={20} color="#fff" />
            </Pressable>
          )}
          
          {/* Drop-Buttons für alle anderen Artikel im Drag-Modus */}
          {isDragging && !isDragged && !item.is_purchased && isDragDropEnabled && (
            <View style={styles.dropButtonsContainer}>
              <Pressable
                style={styles.dropButton}
                onPress={() => handleDrop(item, storeId, 'above')}
              >
                <Ionicons name="arrow-up" size={16} color="#fff" />
              </Pressable>
              <Pressable
                style={styles.dropButton}
                onPress={() => handleDrop(item, storeId, 'below')}
              >
                <Ionicons name="arrow-down" size={16} color="#fff" />
              </Pressable>
            </View>
          )}
          
        </View>
        
      </Pressable>
    </View>
    );
  };

  const handleCompleteShoppingForStore = async (storeId, storeName, items) => {
    
    // Finde abgehakte Artikel für diesen Laden
    // Debug-Log entfernt - war hier
    const purchasedItems = items.filter(item => item.is_purchased);
    
    if (purchasedItems.length === 0) {
      Alert.alert('Info', 'Keine abgehakten Artikel in diesem Laden');
      return;
    }

    
    // Für Web: Direkt ausführen, da Alert.alert nicht zuverlässig funktioniert
    if (typeof window !== 'undefined' && window.navigator) {
      try {
        // WICHTIG: Keine AsyncStorage-Speicherung mehr nötig!
        // Das Backend aktualisiert automatisch die Artikel-Stammdaten (last_used_store_id, default_quantity)
        // beim Aufräumen über die clearPurchasedItems-Route.
        
        const { clearPurchasedItems } = await import('../services/shoppingService');
        const result = await clearPurchasedItems(storeId);
        
        if (result.success) {
          // Liste neu laden um sicherzustellen, dass last_used_store_name korrekt ist
          await loadLists();
          
        } else {
          console.error('❌ Fehler beim Entfernen der Artikel:', result.error);
        }
      } catch (error) {
        console.error('Error completing shopping for store:', error);
      }
    } else {
      // Für native Apps: Alert verwenden
      Alert.alert(
        'Einkauf abschließen',
        `Möchten Sie ${purchasedItems.length} abgehakte Artikel aus ${storeName} entfernen?\n\n${purchasedItems.slice(0, 3).map(item => `• ${item.article_name}`).join('\n')}${purchasedItems.length > 3 ? '\n• ...' : ''}`,
        [
          { text: 'Abbrechen', style: 'cancel' },
          { 
            text: 'Abschließen', 
            style: 'default',
            onPress: async () => {
              try {
                // Batch-Löschung: Alle abgehakten Artikel dieses Ladens auf einmal entfernen
                const { clearPurchasedItems } = await import('../services/shoppingService');
                const result = await clearPurchasedItems(storeId);
                
                if (result.success) {
                  // Lerne Ladenzuordnung: Für alle gekauften Artikel den Laden merken
                  try {
                    const lastUsedStoresData = await AsyncStorage.getItem('lastUsedStoresPerArticle');
                    const lastUsedStores = lastUsedStoresData ? JSON.parse(lastUsedStoresData) : {};
                    
                    // Für jeden gekauften Artikel den Laden speichern
                    purchasedItems.forEach(item => {
                      lastUsedStores[item.article_id] = item.store_id;
                    });
                    
                    await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(lastUsedStores));
                  } catch (error) {
                    console.error('Fehler beim Speichern der Ladenzuordnungen:', error);
                  }
                  
                  // Liste neu laden um sicherzustellen, dass last_used_store_name korrekt ist
                  await loadLists();
                  
                  Alert.alert(
                    'Einkauf abgeschlossen', 
                    `${result.data.deleted_count} Artikel aus ${storeName} entfernt und in Historie gespeichert`
                  );
                } else {
                  Alert.alert('Fehler', 'Fehler beim Entfernen der Artikel');
                }
              } catch (error) {
                console.error('Error completing shopping for store:', error);
                Alert.alert('Fehler', 'Fehler beim Abschließen des Einkaufs');
              }
            }
          }
        ]
      );
    }
  };

  const handleToggleItem = async (item) => {
    try {
      
      if (!item.id) {
        console.error('❌ item.id is undefined!', item);
        return;
      }
      
      // OPTIMISTIC UI: Sofort lokale Änderung
      const newPurchasedState = !item.is_purchased;
      
      // WICHTIG: _needsSync nur setzen wenn offline ODER wenn Sync fehlschlägt
      // Wenn online: Erst versuchen zu synchronisieren, dann _needsSync setzen falls fehlgeschlagen
      const shouldShowSyncIndicator = isOffline; // Nur wenn offline
      
      // Sofort UI aktualisieren
      // WICHTIG: Keine Sortierung anwenden! Die Backend-Sortierung ist korrekt:
      // - is_purchased ASC (nicht abgehakt zuerst)
      // - sort_order ASC (manuelle Sortierung bleibt erhalten)
      // - name ASC (Fallback)
      // Abgehakte Artikel rutschen automatisch nach unten durch is_purchased = true
      setLists(prevLists => {
        const updatedLists = prevLists.map(store => {
          const updatedItems = store.items.map(storeItem => {
            if (storeItem.id === item.id) {
              return { 
                ...storeItem, 
                is_purchased: newPurchasedState,
                _needsSync: shouldShowSyncIndicator, // Nur setzen wenn offline
                _syncTimestamp: shouldShowSyncIndicator ? Date.now() : null // Zeitstempel nur wenn offline
              };
            }
            return storeItem;
          });

          // WICHTIG: Sortierung basierend auf User-Einstellung anwenden
          // Die sort_order Werte müssen erhalten bleiben (außer bei alphabetischer Sortierung)
          // Abgehakte Artikel rutschen automatisch nach unten durch is_purchased = true
          const sorting = user?.shoppingListSorting || 'smart';
          const sortedItems = sortItems(updatedItems, sorting);

          return {
            ...store,
            items: sortedItems
          };
        });
        return updatedLists;
      });
      
      // Lerne Ladenzuordnung: Wenn Artikel gekauft wird, merke dir den Laden
      if (newPurchasedState) { // Artikel wird gekauft
        try {
          const lastUsedStoresData = await AsyncStorage.getItem('lastUsedStoresPerArticle');
          const lastUsedStores = lastUsedStoresData ? JSON.parse(lastUsedStoresData) : {};
          
          // Speichere: Dieser Artikel wurde in diesem Laden gekauft
          lastUsedStores[item.article_id] = item.store_id;
          await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(lastUsedStores));
          
        } catch (error) {
          console.error('Fehler beim Speichern der Ladenzuordnung:', error);
        }
      }
      
      // WICHTIG: Synchronisierung komplett im Hintergrund (OHNE await)
      // Die UI ist bereits aktualisiert (Optimistic UI), die Synchronisierung läuft asynchron
      if (isOnline) {
        // Online: Hintergrund-Sync starten (nicht blockierend)
        // Wenn Sync erfolgreich ist, wird _needsSync automatisch entfernt
        // Wenn Sync fehlschlägt, wird _needsSync gesetzt
        syncItemInBackground(item.id, newPurchasedState).catch(error => {
          // Bei Fehler: _needsSync setzen
          setPendingSyncItems(prev => new Set([...prev, item.id]));
          setLists(prevLists => {
            return prevLists.map(store => ({
              ...store,
              items: store.items.map(storeItem => {
                if (storeItem.id === item.id) {
                  return {
                    ...storeItem,
                    _needsSync: true, // Jetzt setzen weil Sync fehlgeschlagen
                    _syncTimestamp: Date.now()
                  };
                }
                return storeItem;
              })
            }));
          });
        });
      } else {
        // Offline: Item zur Pending-Sync-Liste hinzufügen und Hintergrund-Sync starten
        setPendingSyncItems(prev => new Set([...prev, item.id]));
        syncItemInBackground(item.id, newPurchasedState);
      }
      
    } catch (error) {
      console.error('Error toggling item:', error);
      
      // Bei kritischem Fehler: UI zurücksetzen
      setLists(prevLists => {
        const updatedLists = prevLists.map(store => {
          const updatedItems = store.items.map(storeItem => {
            if (storeItem.id === item.id) {
              return { 
                ...storeItem, 
                is_purchased: item.is_purchased // Zurück zum ursprünglichen Zustand
              };
            }
            return storeItem;
          });

          return {
            ...store,
            items: updatedItems
          };
        });
        return updatedLists;
      });
      
      Alert.alert('Fehler', 'Fehler beim Aktualisieren des Artikels');
    }
  };

  // Hintergrund-Synchronisierung für einzelne Items
  const syncItemInBackground = async (itemId, purchasedState) => {
    try {
      const result = await purchaseItem(itemId, purchasedState);
      
      if (result.success) {
        // Sync erfolgreich - aus Pending-Liste entfernen
        setPendingSyncItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        
        // _needsSync Markierung entfernen
        setLists(prevLists => {
          return prevLists.map(store => ({
            ...store,
            items: store.items.map(storeItem => {
              if (storeItem.id === itemId) {
                return {
                  ...storeItem,
                  _needsSync: false,
                  _syncTimestamp: null
                };
              }
              return storeItem;
            })
          }));
        });
      } else {
      }
    } catch (error) {
      console.error('Background sync error for item', itemId, error);
    }
  };

  // Periodische Hintergrund-Synchronisierung alle 30 Sekunden
  useEffect(() => {
    const syncInterval = setInterval(async () => {
      if (pendingSyncItems.size > 0 && !syncInProgress) {
        setSyncInProgress(true);
        
        try {
          // Alle pending Items synchronisieren
          const syncPromises = Array.from(pendingSyncItems).map(async (itemId) => {
            const item = lists.flatMap(store => store.items).find(i => i.id === itemId);
            if (item && item._needsSync) {
              return syncItemInBackground(itemId, item.is_purchased);
            }
          });
          
          await Promise.all(syncPromises);
        } catch (error) {
          console.error('Periodic sync error:', error);
        } finally {
          setSyncInProgress(false);
        }
      }
    }, 30000); // 30 Sekunden

    return () => clearInterval(syncInterval);
  }, [pendingSyncItems, syncInProgress, lists]);

  // Bild-Upload-Funktionen
  const handleImageSelection = async (source) => {
    setEditingItem(prev => ({ ...prev, uploadingImage: true }));
    
    try {
      const result = await selectAndUploadImage(source);
      
      if (result.success) {
        setEditingItem(prev => ({ ...prev, imageUrl: result.imageUrl, uploadingImage: false }));
        Alert.alert('Erfolg', 'Bild wurde erfolgreich hochgeladen');
      } else {
        setEditingItem(prev => ({ ...prev, uploadingImage: false }));
        Alert.alert('Fehler', result.error);
      }
    } catch (error) {
      console.error('Image selection error:', error);
      setEditingItem(prev => ({ ...prev, uploadingImage: false }));
      Alert.alert('Fehler', 'Fehler beim Hochladen des Bildes: ' + error.message);
    }
  };

  const showImageOptions = () => {
    
    // Für Web: Direkt Galerie öffnen, da Kamera im Web nicht funktioniert
    if (typeof window !== 'undefined' && window.navigator) {
      handleImageSelection('gallery');
      return;
    }
    
    Alert.alert(
      'Bild auswählen',
      'Woher möchtest du das Bild laden?',
      [
        { text: 'Abbrechen', style: 'cancel' },
        { text: 'Kamera', onPress: () => {
          handleImageSelection('camera');
        }},
        { text: 'Galerie', onPress: () => {
          handleImageSelection('gallery');
        }},
      ]
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  if (lists.length === 0 || lists.every(store => store.items.length === 0)) {
    return (
      <View style={styles.centerContainer}>
        <Ionicons name="cart-outline" size={64} color="#ccc" />
        <Text style={styles.emptyText}>Einkaufsliste ist leer</Text>
        <Text style={styles.emptySubtext}>
          Nutze das ➕ oben rechts um Artikel hinzuzufügen
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Suchleiste - nur anzeigen wenn showSearch true ist */}
      {showSearch && (
        <View style={styles.searchContainer}>
        <View style={styles.searchRow}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search-outline" size={20} color="#666" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Artikel suchen..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#999"
            />
            {searchQuery.length > 0 && (
              <Pressable
                onPress={() => setSearchQuery('')}
                style={styles.clearButton}
              >
                <Ionicons name="close-circle" size={20} color="#666" />
              </Pressable>
            )}
          </View>
          
          {/* Reload-Button */}
          <Pressable
            style={styles.reloadButton}
            onPress={onRefresh}
            disabled={refreshing}
          >
            <Ionicons 
              name="refresh-outline" 
              size={20} 
              color={refreshing ? "#ccc" : "#666"} 
            />
          </Pressable>
        </View>
      </View>
      )}

      {/* Offline-Status-Anzeige */}
      {isOffline && (
        <View style={styles.offlineIndicator}>
          <Ionicons name="cloud-offline" size={16} color="#FF6B6B" />
          <Text style={styles.offlineText}>Offline - Änderungen werden gespeichert</Text>
        </View>
      )}
      {isSyncing && (
        <View style={styles.syncIndicator}>
          <ActivityIndicator size="small" color="#2E7D32" />
          <Text style={styles.syncText}>Synchronisiere Änderungen...</Text>
        </View>
      )}

      <View style={styles.listContainer}>
        <SectionList
          sections={sections}
          renderItem={renderSectionItem}
          renderSectionHeader={renderSectionHeader}
          keyExtractor={(item, index) => item.id || `item-${index}`}
          stickySectionHeadersEnabled={true}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={true}
          bounces={true}
          scrollEnabled={true}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="cart-outline" size={64} color="#ccc" />
              <Text style={styles.emptyText}>Keine Artikel in der Einkaufsliste</Text>
              <Text style={styles.emptySubtext}>Füge Artikel über die Schnellliste hinzu</Text>
            </View>
          }
          refreshControl={
            <RefreshControl 
              refreshing={refreshing} 
              onRefresh={onRefresh}
              colors={['#2E7D32']}
            />
          }
        />
        
      </View>

      {/* Bearbeitungs-Modal */}
      <Modal
        visible={editingItem !== null}
        transparent
        animationType="slide"
        onRequestClose={() => setEditingItem(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {editingItem && (
              <>
                <ScrollView 
                  style={styles.modalScrollView}
                  contentContainerStyle={styles.modalScrollContent}
                  showsVerticalScrollIndicator={true}
                  keyboardShouldPersistTaps="handled"
                >
                  <Text style={styles.modalTitle}>
                    {editingItem.articleName}
                  </Text>

                  {/* Laden-Auswahl */}
                  <View style={styles.modalSection}>
                    <Text style={styles.modalLabel}>Laden:</Text>
                    <TouchableOpacity
                      style={styles.storeSelectButton}
                      onPress={() => setShowStoreModal(true)}
                    >
                      <Text style={styles.storeSelectText}>
                        {editingItem.storeName}
                      </Text>
                      <Ionicons name="chevron-down" size={20} color="#666" />
                    </TouchableOpacity>
                  </View>

                  {/* Menge-Eingabe */}
                  <View style={styles.modalSection}>
                    <View style={styles.quantityLabelRow}>
                      <Text style={styles.modalLabel}>Menge:</Text>
                      <TextInput
                        style={styles.unitInput}
                        value={editingItem.unit}
                        onChangeText={(text) => setEditingItem({...editingItem, unit: text})}
                        placeholder="Einheit"
                      />
                    </View>
                    <View style={styles.quantityInputContainer}>
                      {/* Mengeneingabe */}
                      <TextInput
                        style={styles.quantityInput}
                        value={editingItem.quantity?.toString() || ''}
                        onChangeText={(text) => {
                          // Erlaube beliebigen Text für Menge (z.B. "2-3", "ca. 500g", etc.)
                          setEditingItem({...editingItem, quantity: text});
                        }}
                        placeholder="z.B. 2, 2-3, ca. 500"
                      />
                      {/* Schnelleingabe-Buttons */}
                      <View style={styles.quickInputButtons}>
                        {[1, 2, 3, 4, 5, 6].map(number => (
                          <TouchableOpacity
                            key={number}
                            style={styles.quickInputButton}
                            onPress={() => setEditingItem({...editingItem, quantity: number.toString()})}
                          >
                            <Text style={styles.quickInputButtonText}>{number}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  </View>

                  {/* Tags-Eingabe */}
                  <View style={styles.modalSection}>
                    <Text style={styles.modalLabel}>Tags:</Text>
                    <View style={styles.tagsInputContainer}>
                      {editingItem.tags.filter(tag => tag && tag.trim() !== '').map((tag, index) => (
                        <View key={index} style={styles.tagInputItem}>
                          <Text style={styles.tagInputText}>{tag.trim()}</Text>
                          <TouchableOpacity
                            style={styles.tagRemoveButton}
                            onPress={() => {
                              const newTags = editingItem.tags.filter((_, i) => i !== index);
                              setEditingItem({...editingItem, tags: newTags});
                            }}
                          >
                            <Ionicons name="close" size={16} color="#666" />
                          </TouchableOpacity>
                        </View>
                      ))}
                      <TextInput
                        style={styles.tagInputField}
                        placeholder="Tag hinzufügen..."
                        value={newTagText}
                        onChangeText={(text) => {
                          setNewTagText(text);
                          if (text.includes(',') || text.includes(' ')) {
                            const newTags = text.split(/[,\s]+/).map(tag => tag.trim()).filter(tag => tag.length > 0);
                            setEditingItem({
                              ...editingItem, 
                              tags: [...editingItem.tags, ...newTags]
                            });
                            setNewTagText('');
                          }
                        }}
                        onSubmitEditing={(event) => {
                          const text = event.nativeEvent.text.trim();
                          if (text.length > 0) {
                            setEditingItem({
                              ...editingItem, 
                              tags: [...editingItem.tags, text]
                            });
                            setNewTagText('');
                          }
                        }}
                        multiline={false}
                        autoCapitalize="words"
                        autoCorrect={false}
                      />
                    </View>
                  </View>

                  {/* Notizen-Eingabe */}
                  <View style={styles.modalSection}>
                    <Text style={styles.modalLabel}>Notizen:</Text>
                    <TextInput
                      style={[styles.textInput, styles.notesInput]}
                      value={editingItem.notes}
                      onChangeText={(text) => setEditingItem({...editingItem, notes: text})}
                      placeholder="z.B. Besonders reif, Marke X bevorzugt"
                      multiline={true}
                      numberOfLines={3}
                    />
                  </View>

                  {/* Produktbild */}
                  <View style={styles.modalSection}>
                    <Text style={styles.modalLabel}>Produktbild</Text>
                    
                    {editingItem.imageUrl ? (
                      <View style={styles.imageContainer}>
                        <Image source={{ uri: editingItem.imageUrl }} style={styles.previewImage} />
                        {/* Kleine Icon-Buttons über dem Bild */}
                        <TouchableOpacity
                          style={styles.editImageIcon}
                          onPress={showImageOptions}
                          disabled={editingItem.uploadingImage}
                        >
                          <Ionicons name="create-outline" size={16} color="#fff" />
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.deleteImageIcon}
                          onPress={() => {
                            if (window.confirm('Möchtest du das Bild wirklich löschen?')) {
                              setEditingItem(prev => ({ ...prev, imageUrl: '' }));
                            }
                          }}
                          disabled={editingItem.uploadingImage}
                        >
                          <Ionicons name="trash-outline" size={16} color="#fff" />
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={styles.imagePlaceholder}
                        onPress={showImageOptions}
                        disabled={editingItem.uploadingImage}
                      >
                        {editingItem.uploadingImage ? (
                          <ActivityIndicator size="small" color="#2E7D32" />
                        ) : (
                          <>
                            <Ionicons name="camera" size={32} color="#ccc" />
                            <Text style={styles.placeholderText}>Bild hinzufügen</Text>
                          </>
                        )}
                      </TouchableOpacity>
                    )}
                    
                    <Text style={styles.hint}>Tippe auf das Feld, um ein Bild aus der Kamera oder Galerie auszuwählen</Text>
                  </View>
                </ScrollView>

                {/* Buttons - außerhalb des ScrollView, damit sie immer sichtbar sind */}
                <View style={styles.modalButtons}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.deleteButton]}
                    onPress={async () => {
                      try {
                        const result = await deleteItem(editingItem.id);
                        
                        if (result.success) {
                          // Artikel aus der lokalen Liste entfernen
                          setLists(prevLists => 
                            prevLists.map(store => ({
                              ...store,
                              items: store.items.filter(item => item.id !== editingItem.id)
                            }))
                          );
                          setEditingItem(null);
                          setNewTagText('');
                          Alert.alert('Erfolg', 'Artikel wurde entfernt');
                        } else {
                          Alert.alert('Fehler', result.error);
                        }
                      } catch (error) {
                        console.error('Error deleting item:', error);
                        Alert.alert('Fehler', 'Fehler beim Löschen des Artikels');
                      }
                    }}
                  >
                    <Ionicons name="trash-outline" size={16} color="#fff" />
                    <Text style={styles.deleteButtonText}>Löschen</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.modalButton, styles.cancelButton]}
                    onPress={() => {
                      setEditingItem(null);
                      setNewTagText('');
                    }}
                  >
                    <Text style={styles.cancelButtonText}>Abbrechen</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.modalButton, styles.saveButton]}
                    onPress={async () => {
                      try {
                        
                        // Prüfen ob der Laden geändert wurde
                        const originalItem = lists
                          .flatMap(store => store.items)
                          .find(item => item.id === editingItem.id);
                        
                        // Tags verarbeiten: Bestehende Tags + eventuell noch nicht gespeicherter Text
                        let finalTags = [...editingItem.tags];
                        if (newTagText.trim()) {
                          finalTags.push(newTagText.trim());
                          setNewTagText(''); // Eingabefeld leeren
                        }

                        if (originalItem && originalItem.store_id !== editingItem.storeId) {
                          // Laden wurde geändert - Item verschieben
                          const result = await moveItemToStore(editingItem.id, editingItem.storeId, {
                            quantity: editingItem.quantity,
                            unit: editingItem.unit,
                            tags: finalTags,
                            notes: editingItem.notes,
                            imageUrl: editingItem.imageUrl || ''
                          });
                          
                          if (result.success) {
                            // Liste neu laden (Backend + Socket.io übernehmen die Synchronisation)
                            await loadLists();
                          } else {
                            console.error('Fehler beim Verschieben:', result.error);
                            Alert.alert('Fehler', 'Fehler beim Verschieben des Artikels');
                            return;
                          }
                        } else {
                          // Nur Daten aktualisieren
                          const result = await updateItem(editingItem.id, {
                            quantity: editingItem.quantity,
                            unit: editingItem.unit,
                            tags: finalTags,
                            notes: editingItem.notes,
                            imageUrl: editingItem.imageUrl || ''
                          });
                          
                          if (result.success) {
                            // WICHTIG: Auch den Artikel aktualisieren (wie Tags/Notizen)
                            // Damit die Daten nach dem Aufräumen erhalten bleiben!
                            try {
                              const { updateArticle } = await import('../services/articlesService');
                              const articles = await import('../contexts/ArticleContext');
                              
                              // Finde den Artikel-Namen (wird für updateArticle benötigt)
                              const item = lists.flatMap(s => s.items).find(i => i.id === editingItem.id);
                              if (item) {
                                await updateArticle(item.article_id, {
                                  name: item.article_name, // Name ist erforderlich!
                                  default_quantity: editingItem.quantity,
                                  default_unit: editingItem.unit,
                                  default_tags: finalTags,
                                  notes: editingItem.notes,
                                  image_url: editingItem.imageUrl,
                                  last_used_store_id: editingItem.storeId || item.store_id // Laden speichern!
                                });
                              }
                            } catch (articleError) {
                              console.error('Fehler beim Aktualisieren des Artikels:', articleError);
                              // Nicht kritisch, fortfahren
                            }
                            
                            // Liste neu laden (Backend + Socket.io übernehmen die Synchronisation)
                            await loadLists();
                          } else {
                            console.error('Fehler beim Aktualisieren:', result.error);
                            Alert.alert('Fehler', 'Fehler beim Speichern des Artikels');
                            return;
                          }
                        }
                        
                        setEditingItem(null);
                        setNewTagText('');
                      } catch (error) {
                        console.error('Error saving item:', error);
                        Alert.alert('Fehler', 'Fehler beim Speichern des Artikels');
                      }
                    }}
                  >
                    <Text style={styles.saveButtonText}>Speichern</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>

      {/* Laden-Auswahl-Modal */}
      <Modal
        visible={showStoreModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStoreModal(false)}
      >
        <View style={styles.storeModalOverlay}>
          <View style={styles.storeModalContent}>
            <Text style={styles.storeModalTitle}>Laden auswählen</Text>
            
            <ScrollView style={styles.storeList}>
              {stores.map(store => (
                <TouchableOpacity
                  key={store.id}
                  style={[
                    styles.storeOption,
                    editingItem && editingItem.storeId === store.id && styles.storeOptionSelected
                  ]}
                  onPress={() => {
                    setEditingItem({
                      ...editingItem,
                      storeId: store.id,
                      storeName: store.name
                    });
                    setShowStoreModal(false);
                  }}
                >
                  <Text style={[
                    styles.storeOptionText,
                    editingItem && editingItem.storeId === store.id && styles.storeOptionTextSelected
                  ]}>
                    {store.name}
                  </Text>
                  {editingItem && editingItem.storeId === store.id && (
                    <Ionicons name="checkmark" size={20} color="#2E7D32" />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
            
            <TouchableOpacity
              style={styles.storeModalCloseButton}
              onPress={() => setShowStoreModal(false)}
            >
              <Text style={styles.storeModalCloseText}>Abbrechen</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Bild-Anzeige-Modal */}
      <Modal
        visible={showImageModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowImageModal(false)}
      >
        <View style={styles.imageModalOverlay}>
          <View style={styles.imageModalContent}>
            <TouchableOpacity
              style={styles.imageModalCloseButton}
              onPress={() => setShowImageModal(false)}
            >
              <Ionicons name="close" size={24} color="#fff" />
            </TouchableOpacity>
            {selectedImageUrl && (
              <Image
                source={{ uri: selectedImageUrl }}
                style={styles.imageModalImage}
                resizeMode="contain"
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Update-Hinweis-Modal */}
      <Modal
        visible={showUpdateModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowUpdateModal(false)}
      >
        <View style={styles.updateModalOverlay}>
          <View style={styles.updateModalContent}>
            <View style={styles.updateModalHeader}>
              <Ionicons name="refresh-circle" size={32} color="#2E7D32" />
              <Text style={styles.updateModalTitle}>Neue Version verfügbar</Text>
            </View>
            <Text style={styles.updateModalMessage}>
              Neue App-Version wird installiert...
              {updateInfo && (
                <>
                  {'\n\n'}Version {updateInfo.serverVersion}
                  {'\n\n'}Deine Änderungen werden gesichert.
                </>
              )}
            </Text>
            <ActivityIndicator size="large" color="#2E7D32" style={{ marginTop: 20 }} />
          </View>
        </View>
      </Modal>

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 20,
  },
  listContainer: {
    flex: 1,
  },
  listContent: {
    padding: 10,
    flexGrow: 1,
  },
  storeSection: {
    marginBottom: 15,
  },
  storeSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#4CAF50',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginBottom: 8,
    justifyContent: 'space-between',
    // Wichtig für Sticky Headers
    zIndex: 10,
  },
  storeTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  completeStoreButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 3,
    flexDirection: 'row',
    alignItems: 'center',
  },
  storeIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E8F5E9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  storeSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  itemCount: {
    fontSize: 12,
    fontWeight: '500',
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  emptyStore: {
    backgroundColor: '#f9f9f9',
    padding: 15,
    borderRadius: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderStyle: 'dashed',
  },
  emptyStoreText: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
  itemCard: {
    backgroundColor: '#fff',
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 1,
    elevation: 1,
    overflow: 'hidden',
  },
  itemCardPurchased: {
    backgroundColor: '#f0f7f0',
    borderLeftWidth: 3,
    borderLeftColor: '#4CAF50',
  },
  itemCardDragged: {
    opacity: 0.7,
    transform: [{ scale: 1.1 }],
    elevation: 12,
    shadowColor: '#2E7D32',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.4,
    shadowRadius: 12,
    borderColor: '#2E7D32',
    borderWidth: 2,
    backgroundColor: '#F1F8E9',
  },
  itemCardDragOver: {
    borderColor: '#FF9800',
    borderWidth: 3,
    backgroundColor: '#FFF3E0',
    transform: [{ scale: 1.02 }],
    elevation: 4,
    shadowColor: '#FF9800',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
  },
  itemCardNew: {
    borderColor: '#2196F3',
    borderWidth: 2,
    backgroundColor: '#E3F2FD',
    shadowColor: '#2196F3',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  dropZone: {
    height: 20,
    marginVertical: 4,
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    borderWidth: 3,
    borderColor: '#4CAF50',
    borderStyle: 'dashed',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dropZoneActive: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    borderColor: '#4CAF50',
  },
  dropZoneCrossStore: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    borderColor: '#4CAF50',
    borderWidth: 3,
  },
  dropZoneIndicatorCrossStore: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    borderColor: '#4CAF50',
    borderWidth: 3,
  },
  itemTouchable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxContainer: {
    padding: 10,
    paddingLeft: 8,
  },
  dragButton: {
    padding: 10,
    paddingRight: 8,
  },
  dragCancelButton: {
    backgroundColor: '#FF6B6B',
    borderRadius: 15,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  dropButtonsContainer: {
    flexDirection: 'row',
    marginLeft: 8,
    gap: 4,
  },
  dropButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemContent: {
    flex: 1,
  },
  itemMainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  itemLeftColumn: {
    flex: 1,
    flexDirection: 'column',
    marginRight: 8,
  },
  itemNameWithTags: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  itemName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  itemNamePurchased: {
    textDecorationLine: 'line-through',
    color: '#999',
  },
  quantityContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  itemQuantityNumber: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
    lineHeight: 18,
  },
  itemQuantityNumberPurchased: {
    color: '#999',
  },
  itemQuantityUnit: {
    fontSize: 12,
    color: '#666',
    lineHeight: 14,
    marginTop: 2,
  },
  itemQuantityUnitPurchased: {
    color: '#999',
  },
  itemSecondaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginLeft: 6,
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
    marginRight: 3,
  },
  tagText: {
    fontSize: 9,
    color: '#2E7D32',
    fontWeight: '600',
  },
  tagTextPurchased: {
    color: '#999',
  },
  itemNotes: {
    fontSize: 11,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 1,
    marginBottom: 1,
  },
  itemNotesPurchased: {
    color: '#999',
  },
  emptyText: {
    fontSize: 20,
    color: '#999',
    marginTop: 20,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 10,
    textAlign: 'center',
  },
  emptyButton: {
    backgroundColor: '#2E7D32',
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 20,
  },
  emptyButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  quickListFab: {
    position: 'absolute',
    right: 20,
    bottom: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#2E7D32',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  itemActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  editButton: {
    padding: 8,
    marginRight: 4,
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxHeight: '85%',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalScrollView: {
    flex: 1,
  },
  modalScrollContent: {
    padding: 16,
    paddingBottom: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalSection: {
    marginBottom: 12,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  quantityInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  quantityInput: {
    width: 60,
    minWidth: 60,
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 4,
    paddingVertical: 8,
    fontSize: 20,
    fontWeight: '700',
    color: '#2E7D32',
    textAlign: 'center',
    borderWidth: 2,
    borderColor: '#2E7D32',
  },
  quantityUnit: {
    fontSize: 14,
    color: '#666',
    marginLeft: 8,
  },
  tagsInputContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  tagInputItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 4,
    marginBottom: 4,
  },
  tagInputText: {
    fontSize: 12,
    color: '#2E7D32',
    marginRight: 4,
  },
  tagRemoveButton: {
    padding: 2,
  },
  tagInputField: {
    flex: 1,
    minWidth: 120,
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  textInput: {
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  notesInput: {
    height: 80,
    textAlignVertical: 'top',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 8,
    padding: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#fff',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  deleteButton: {
    backgroundColor: '#d32f2f',
    flex: 0.8,
  },
  deleteButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    marginLeft: 4,
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
  },
  saveButton: {
    backgroundColor: '#2E7D32',
  },
  saveButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
  // Neue Styles für Bildanzeige
  itemNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  imageIconButton: {
    padding: 4,
    marginLeft: 8,
  },
  imageModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalContent: {
    width: '90%',
    height: '80%',
    position: 'relative',
  },
  imageModalCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 20,
    padding: 8,
  },
  imageModalImage: {
    width: '100%',
    height: '100%',
  },
  // Styles für Laden-Auswahl
  storeSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  storeSelectText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  // Styles für Laden-Auswahl-Modal
  storeModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  storeModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '80%',
    maxHeight: '70%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  storeModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 15,
    textAlign: 'center',
  },
  storeList: {
    maxHeight: 300,
  },
  storeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderRadius: 8,
    marginBottom: 5,
    backgroundColor: '#f5f5f5',
  },
  storeOptionSelected: {
    backgroundColor: '#E8F5E9',
    borderWidth: 1,
    borderColor: '#2E7D32',
  },
  storeOptionText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  storeOptionTextSelected: {
    color: '#2E7D32',
    fontWeight: '600',
  },
  storeModalCloseButton: {
    backgroundColor: '#f5f5f5',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginTop: 15,
    alignItems: 'center',
  },
  storeModalCloseText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  searchContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 0,
    outline: 'none',
    borderWidth: 0,
  },
  clearButton: {
    marginLeft: 8,
    padding: 2,
  },
  reloadButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 40,
    minHeight: 40,
  },
  quickInputButtons: {
    flexDirection: 'row',
    gap: 4,
  },
  quickInputButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  quickInputButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  quantityInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  quantityLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  quantityUnitLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  unitInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    color: '#333',
    backgroundColor: '#f9f9f9',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
    paddingHorizontal: 20,
  },
  emptyText: {
    fontSize: 18,
    color: '#666',
    marginTop: 16,
    fontWeight: '500',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 8,
    textAlign: 'center',
  },
  
  // Offline-Status-Styles
  offlineIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFEBEE',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: '#FF6B6B',
  },
  offlineText: {
    color: '#D32F2F',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  syncIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E8',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: '#2E7D32',
  },
  syncText: {
    color: '#2E7D32',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  pendingChangesIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF3E0',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: '#FFA726',
  },
  pendingChangesText: {
    color: '#F57C00',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  offlineChangeIndicator: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#FFF3E0',
    borderRadius: 8,
    padding: 2,
    borderWidth: 1,
    borderColor: '#FFA726',
  },
  // Bild-Upload Styles
  imageContainer: {
    alignItems: 'center',
    marginBottom: 10,
    position: 'relative',
  },
  previewImage: {
    width: 120,
    height: 120,
    borderRadius: 8,
    marginBottom: 10,
  },
  editImageIcon: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 0,
    borderBottomLeftRadius: 8,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteImageIcon: {
    position: 'absolute',
    bottom: 10,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 0,
    borderBottomRightRadius: 8,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholder: {
    width: 120,
    height: 120,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#e0e0e0',
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  placeholderText: {
    color: '#999',
    marginTop: 8,
    fontSize: 12,
    textAlign: 'center',
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 5,
    textAlign: 'center',
  },
  // Drop-Indikator Styles
  // Escape-Button Styles
  escapeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF6B6B',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    marginTop: 8,
    alignSelf: 'center',
  },
  escapeButtonText: {
    color: '#fff',
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '500',
  },
  // Update-Hinweis-Modal Styles
  updateModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  updateModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '85%',
    maxWidth: 400,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  updateModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  updateModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginLeft: 8,
  },
  updateModalMessage: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
  },
  updateModalButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  updateModalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 6,
    alignItems: 'center',
  },
  updateModalButtonPrimary: {
    backgroundColor: '#2E7D32',
  },
  updateModalButtonSecondary: {
    backgroundColor: '#f5f5f5',
  },
  updateModalButtonTextPrimary: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  updateModalButtonTextSecondary: {
    color: '#666',
    fontSize: 16,
    fontWeight: '500',
  },
});

export default ShoppingScreen;


